/**
 * Cómo ve el cliente el estado de un pedido: UNA sola tabla para la píldora de la columna Estado
 * (Pedido.textoEstadoCliente / estadoAgrupadoCliente) y para el filtro de Estado del listado
 * (Pedido.opcionesFiltroEstado + EstadoPedidoClienteFilter), para que lo que se ve y lo que se filtra no
 * puedan divergir.
 *
 * Sin imports a propósito: la usan el modelo Pedido y el filtro, y el modelo usa el filtro en su decorador (un
 * import circular dejaría algo sin definir).
 */

/** Lo que ve el cliente de un pedido En tránsito o Entregado: el detalle va en la columna Envío */
export const TEXTO_ENVIADO = 'ENVIADO';

/** Lo que ve el cliente mientras CNC le pregunta por alguna pieza del pedido */
export const TEXTO_PAUSADO = 'PAUSADO';

/**
 * Los 17 estados reales del pedido (EstadoPedido en cnc-api/src/Entity/Pedidos/EstadoPedido.php), en su orden, con
 * el estado agrupado que ve el cliente: de él sale la clase de color de la píldora. El texto de la píldora es el
 * agrupado, salvo En tránsito y Entregado, que se leen ENVIADO (con su color: gris y verde).
 * - Validado cuenta como subido; el parcialmente validado, en cambio, se ve VALIDADO (así salía ya).
 * - Lo que está en fábrica es EN PRODUCCIÓN: un parcial lo da la línea más avanzada y las demás pueden seguir en
 *   fábrica, así que los parciales de logística, listo para envío y en tránsito siguen en producción. LISTO PARA
 *   ENVÍO y ENVIADO solo con todas las líneas ahí.
 * - PAUSADO no está: no es un estado del pedido sino `pausado` (lo calcula la API). Al filtrar por Estado, un pedido
 *   pausado sale en la opción de su estado real (casi siempre EN PRODUCCIÓN), aunque su píldora diga PAUSADO.
 */
export const ESTADOS_PEDIDO_CLIENTE: ReadonlyArray<{ estado: string, agrupado: string }> = [
    {estado: 'En curso', agrupado: 'EN CURSO'},
    {estado: 'Subido a CNC', agrupado: 'SUBIDO A CNC'},
    {estado: 'Pendiente de validación', agrupado: 'PENDIENTE DE VALIDACIÓN'},
    {estado: 'Parcialmente validado', agrupado: 'VALIDADO'},
    {estado: 'Validado', agrupado: 'SUBIDO A CNC'},
    {estado: 'Parcialmente en producción', agrupado: 'EN PRODUCCIÓN'},
    {estado: 'En producción', agrupado: 'EN PRODUCCIÓN'},
    {estado: 'Parcialmente en logística', agrupado: 'EN PRODUCCIÓN'},
    {estado: 'En logística', agrupado: 'EN PRODUCCIÓN'},
    {estado: 'Parcialmente listo para envío', agrupado: 'EN PRODUCCIÓN'},
    {estado: 'Listo para envío', agrupado: 'LISTO PARA ENVÍO'},
    {estado: 'Parcialmente en tránsito', agrupado: 'EN PRODUCCIÓN'},
    {estado: 'En tránsito', agrupado: 'EN TRÁNSITO'},
    {estado: 'Entregado', agrupado: 'ENTREGADO'},
    {estado: 'Parcialmente rechazado', agrupado: 'RECHAZADO'},
    {estado: 'Rechazado', agrupado: 'RECHAZADO'},
    {estado: 'Cancelado', agrupado: 'CANCELADO'},
];

/** Los agrupados que se leen ENVIADO */
const AGRUPADOS_ENVIADO = ['EN TRÁNSITO', 'ENTREGADO'];

/**
 * El estado agrupado (sin pausa) de un estado real. Uno que no esté en la tabla (un estado nuevo de la API) sale como
 * salía antes: en mayúsculas y sin «Parcialmente».
 */
export function agrupadoCliente(estado: string): string {
    const fila = ESTADOS_PEDIDO_CLIENTE.find(f => f.estado === estado);
    if (fila) {
        return fila.agrupado;
    }
    estado = estado ?? '';
    return (estado.indexOf('Parcial') >= 0 ? estado.substring(13) : estado).toUpperCase();
}

/** El texto de la píldora a partir del estado agrupado: En tránsito y Entregado, ENVIADO */
export function textoDeAgrupado(agrupado: string): string {
    return AGRUPADOS_ENVIADO.includes(agrupado) ? TEXTO_ENVIADO : agrupado;
}

/**
 * Textos de la píldora que NO se ofrecen como opción del filtro de Estado (decisión del usuario, 2026-10-20): sus
 * estados reales (Pendiente de validación, Parcialmente validado y Cancelado) siguen viéndose igual en la tabla, pero
 * no se pueden filtrar desde el panel. Una URL antigua con ellos sigue filtrando (ver estadosDeOpcion) y sale como
 * una etiqueta que se puede quitar.
 */
export const TEXTOS_SIN_OPCION_DE_FILTRO = ['PENDIENTE DE VALIDACIÓN', 'VALIDADO', 'CANCELADO'];

/**
 * Las opciones del filtro de Estado: cada texto distinto de la píldora una vez, en el orden de los estados y sin
 * TEXTOS_SIN_OPCION_DE_FILTRO (EN CURSO, SUBIDO A CNC, EN PRODUCCIÓN, LISTO PARA ENVÍO, ENVIADO, RECHAZADO). La opción
 * es el propio texto: lo que se marca es lo que se ve en la tabla.
 */
export function opcionesEstadoCliente(): string[] {
    const opciones: string[] = [];
    for (const {agrupado} of ESTADOS_PEDIDO_CLIENTE) {
        const texto = textoDeAgrupado(agrupado);
        if (!opciones.includes(texto) && !TEXTOS_SIN_OPCION_DE_FILTRO.includes(texto)) {
            opciones.push(texto);
        }
    }
    return opciones;
}

/**
 * Los estados reales que el cliente ve con ese texto de píldora; vacío si no es ninguno. Vale también para los textos
 * que ya no son opción (TEXTOS_SIN_OPCION_DE_FILTRO): una URL guardada con ellos sigue filtrando lo mismo.
 */
export function estadosDeOpcion(opcion: string): string[] {
    return ESTADOS_PEDIDO_CLIENTE
        .filter(f => textoDeAgrupado(f.agrupado) === opcion)
        .map(f => f.estado);
}
